import type {
  ServerWorkbookDeleteImpact,
  ServerWorkbookDeleteImpactDatasource,
} from '../../data/ServerApiClient'

type WorkbookDeleteDialogProps = {
  open: boolean
  workbookName: string
  accountingCompanyName?: string | null
  accountingProviderName?: string | null
  impact: ServerWorkbookDeleteImpact | null
  loading?: boolean
  busy?: boolean
  error?: string | null
  deleteUnsharedDatasources: boolean
  onToggleDeleteUnsharedDatasources: (next: boolean) => void
  onCancel: () => void
  onConfirm: () => void | Promise<void>
}

const formatDatasourceScope = (entry: ServerWorkbookDeleteImpactDatasource): string => {
  if (entry.other_workbook_count > 0) {
    return `Shared with ${entry.other_workbook_count} other ${entry.other_workbook_count === 1 ? 'workbook' : 'workbooks'}`
  }
  if (entry.can_delete_with_workbook) return 'Only referenced by this workbook'
  return 'Only referenced by this workbook, but cannot be deleted automatically'
}

export function WorkbookDeleteDialog({
  open,
  workbookName,
  accountingCompanyName = null,
  accountingProviderName = null,
  impact,
  loading = false,
  busy = false,
  error = null,
  deleteUnsharedDatasources,
  onToggleDeleteUnsharedDatasources,
  onCancel,
  onConfirm,
}: WorkbookDeleteDialogProps) {
  if (!open) return null

  const resolvedWorkbookName = String(
    impact?.workbook_name || workbookName || 'Untitled Workbook',
  ).trim() || 'Untitled Workbook'
  const datasourceRows = Array.isArray(impact?.datasources) ? impact!.datasources : []
  const accountingCompany = String(accountingCompanyName || '').trim()
  const accountingProvider = String(accountingProviderName || 'accounting provider').trim()

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40000,
        background: 'rgba(17, 24, 39, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => {
        if (busy) return
        onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: 'min(680px, 96vw)',
          maxHeight: 'min(80vh, 760px)',
          overflow: 'auto',
          borderRadius: 12,
          border: '1px solid #d1d5db',
          background: '#ffffff',
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
          Delete workbook?
        </div>
        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
          Delete workbook "{resolvedWorkbookName}".
          {accountingCompany ? ` All imported ${accountingProvider} data for ${accountingCompany}, its canonical financial facts, generated artifacts, connection credentials that are not shared with another company, and billing registration will also be permanently removed.` : ' The workbook and its workbook-scoped refresh jobs will be removed.'}
          If you also choose to delete unshared datasources, their published artifact files in storage will be deleted too.
        </div>

        {error ? (
          <div style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', fontSize: 12 }}>
            {error}
          </div>
        ) : null}

        {loading && !impact ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>Loading delete impact…</div>
        ) : impact ? (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: 8,
              }}
            >
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Datasources</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{impact.datasource_count}</div>
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Shared datasources</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{impact.shared_datasource_count}</div>
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Published artifacts</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{impact.published_artifact_count}</div>
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Refresh jobs</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{impact.workbook_refresh_job_count}</div>
              </div>
            </div>

            {impact.datasource_count > 0 ? (
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={deleteUnsharedDatasources}
                    onChange={(e) => onToggleDeleteUnsharedDatasources(e.target.checked)}
                    disabled={busy || impact.exclusive_deletable_datasource_count <= 0}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                      Also delete unshared datasources owned only by this workbook
                    </div>
                    <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5 }}>
                      Shared datasources will be retained. Unshared datasources can be deleted only if you also have edit access to them.
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      Deletable now: {impact.exclusive_deletable_datasource_count}
                      {' · '}
                      Retained because shared or protected: {impact.shared_datasource_count + impact.exclusive_retained_datasource_count}
                    </div>
                  </div>
                </div>
              </label>
            ) : null}

            {datasourceRows.length > 0 ? (
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontSize: 12, fontWeight: 800, color: '#111827' }}>
                  Related datasources
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {datasourceRows.map((entry) => {
                    const willDelete = deleteUnsharedDatasources && entry.can_delete_with_workbook
                    return (
                      <div
                        key={entry.datasource_id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) auto',
                          gap: 10,
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderTop: '1px solid #f3f4f6',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entry.datasource_name}
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>
                            {entry.datasource_kind} · {formatDatasourceScope(entry)}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          {entry.extract_run_count} runs · {entry.published_artifact_count} published artifacts
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: willDelete ? '#b91c1c' : '#4b5563',
                            background: willDelete ? '#fff1f2' : '#f3f4f6',
                            border: `1px solid ${willDelete ? '#fecaca' : '#e5e7eb'}`,
                            borderRadius: 999,
                            padding: '4px 8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {willDelete ? 'Delete datasource' : 'Keep datasource'}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              border: '1px solid #d1d5db',
              background: '#ffffff',
              color: '#111827',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { void onConfirm() }}
            disabled={busy || loading}
            style={{
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 700,
              cursor: busy || loading ? 'not-allowed' : 'pointer',
              opacity: busy || loading ? 0.7 : 1,
              border: '1px solid #fecaca',
              background: '#dc2626',
              color: '#ffffff',
            }}
          >
            {busy ? 'Deleting…' : accountingCompany ? 'Permanently delete' : 'Delete workbook'}
          </button>
        </div>
      </div>
    </div>
  )
}
